import React, { useCallback, useEffect, useRef, useState } from 'react';
import { listCourses, courseVideoUrl, fetchCourseVtt } from '@/api/client';
import type { CourseList, CourseSummary } from '@/api/client';
import { formatTime, parseVtt } from '@/lib/vtt';
import type { VttCue } from '@/lib/vtt';
import {
  AlertTriangle, ArrowLeft, GraduationCap, Info, Loader2,
} from 'lucide-react';

import { apiErrorText } from '../../lib/describeApiError';
/**
 * /courses — the owner's user-guide videos. The server reads them from
 * the folder COURSE_VIDEOS_DIR points at; the catalogue is listed in
 * full even before the files are there, so drafters can see what is
 * coming. Open to every signed-in user.
 */

interface Props {
  onBack?: () => void;
}

const LAST_LESSON_KEY = 'courses_last_lesson';

// After the drafter scrolls the transcript by hand, leave it alone for
// this long so reading ahead is not undone by the next spoken line.
const MANUAL_SCROLL_GRACE_MS = 4000;

const outlineBtn =
  'inline-flex h-9 items-center justify-center gap-1 rounded-sm border border-zinc-300 ' +
  'bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 transition-colors';

const sectionHeading = 'mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500';

function readLastLesson(): string | null {
  try {
    return localStorage.getItem(LAST_LESSON_KEY);
  } catch {
    return null;
  }
}

function rememberLesson(id: string) {
  try {
    localStorage.setItem(LAST_LESSON_KEY, id);
  } catch {
    // Private mode or storage disabled — the page just forgets.
  }
}

/** The remembered lesson while it can still be played, otherwise the
 * first one that can. */
function initialLesson(courses: CourseSummary[]): string | null {
  const playable = courses.filter((c) => c.available);
  const last = readLastLesson();
  return (playable.find((c) => c.id === last) ?? playable[0])?.id ?? null;
}

/** Player, details, chapters and transcript for one lesson. Keyed by the
 * lesson id, so switching lessons starts it fresh. */
function LessonPlayer({ course }: { course: CourseSummary }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const trackRef = useRef<HTMLTrackElement>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const manualScrollAt = useRef(0);
  const [subtitleUrl, setSubtitleUrl] = useState<string | null>(null);
  const [cues, setCues] = useState<VttCue[]>([]);
  const [chapters, setChapters] = useState<VttCue[]>([]);
  const [activeCue, setActiveCue] = useState(-1);
  const [videoFailed, setVideoFailed] = useState(false);

  useEffect(() => {
    if (!course.has_subtitles) return;
    let cancelled = false;
    let blobUrl: string | null = null;
    fetchCourseVtt(course.id, 'subtitles')
      .then((text) => {
        if (cancelled) return;
        blobUrl = URL.createObjectURL(new Blob([text], { type: 'text/vtt' }));
        setSubtitleUrl(blobUrl);
        setCues(parseVtt(text));
      })
      .catch(() => {
        // Subtitles are an extra; the lesson plays without them.
      });
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [course.id, course.has_subtitles]);

  useEffect(() => {
    if (!course.has_chapters) return;
    let cancelled = false;
    fetchCourseVtt(course.id, 'chapters')
      .then((text) => {
        if (!cancelled) setChapters(parseVtt(text));
      })
      .catch(() => {
        // No chapter list then; the video is unaffected.
      });
    return () => { cancelled = true; };
  }, [course.id, course.has_chapters]);

  // The track arrives after the video has started loading, and a
  // browser that has already made its pick of default tracks ignores
  // `default` on a late one. Turn it on once here; after that the
  // captions button is the drafter's.
  useEffect(() => {
    const track = trackRef.current?.track;
    if (subtitleUrl && track) track.mode = 'showing';
  }, [subtitleUrl]);

  // timeupdate fires several times a second. Setting the same index
  // again is a no-op for React, so the transcript re-renders only when
  // the spoken line changes.
  const syncActiveCue = () => {
    const video = videoRef.current;
    if (!video || cues.length === 0) return;
    const t = video.currentTime;
    setActiveCue(cues.findIndex((c) => t >= c.start && t < c.end));
  };

  const keepActiveCueVisible = useCallback(() => {
    const video = videoRef.current;
    const box = transcriptRef.current;
    // Only while the video plays: a paused drafter is reading, not
    // following along.
    if (activeCue < 0 || !video || video.paused || !box) return;
    if (Date.now() - manualScrollAt.current < MANUAL_SCROLL_GRACE_MS) return;
    const row = box.querySelector<HTMLElement>(`[data-cue="${activeCue}"]`);
    if (!row) return;
    // The box is `relative`, so a row's offsetTop is measured from its
    // top. Moving scrollTop scrolls the box alone; scrollIntoView would
    // drag the page along with it.
    const top = row.offsetTop;
    const bottom = top + row.offsetHeight;
    if (top < box.scrollTop || bottom > box.scrollTop + box.clientHeight) {
      box.scrollTop = Math.max(0, top - box.clientHeight / 3);
    }
  }, [activeCue]);

  useEffect(() => { keepActiveCueVisible(); }, [keepActiveCueVisible]);

  const noteManualScroll = () => { manualScrollAt.current = Date.now(); };

  const seek = (seconds: number) => {
    const video = videoRef.current;
    if (video) video.currentTime = seconds;
  };

  return (
    <div>
      <video
        key={course.id}
        ref={videoRef}
        controls
        preload="metadata"
        src={courseVideoUrl(course.id)}
        playsInline
        className="w-full bg-black rounded-sm"
        data-testid="course-video"
        onTimeUpdate={syncActiveCue}
        onPlay={keepActiveCueVisible}
        onError={() => setVideoFailed(true)}
      >
        {subtitleUrl && (
          <track
            ref={trackRef}
            kind="subtitles"
            srcLang="en"
            label="English"
            src={subtitleUrl}
            default
          />
        )}
      </video>
      {videoFailed && (
        <div
          className="mt-2 p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-800 flex items-start gap-2"
          data-testid="course-video-error"
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          This video could not be played. It may be missing on the server or
          in a format this browser does not support.
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="space-y-6">
          <div>
            <h2
              className="text-lg font-semibold tracking-tight text-zinc-900"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
            >
              <span className="font-mono text-sm text-zinc-500 mr-2">{course.id}</span>{' '}
              {course.title}
            </h2>
            {course.duration && (
              <div className="mt-0.5 text-xs text-zinc-500 tabular-nums">{course.duration}</div>
            )}
            <dl className="mt-3 space-y-3 text-sm">
              {course.summary && (
                <div>
                  <dt className="text-xs font-medium text-zinc-500">What it covers</dt>
                  <dd className="text-zinc-700">{course.summary}</dd>
                </div>
              )}
              {course.audience && (
                <div>
                  <dt className="text-xs font-medium text-zinc-500">For</dt>
                  <dd className="text-zinc-700">{course.audience}</dd>
                </div>
              )}
            </dl>
          </div>

          {chapters.length > 0 && (
            <section aria-label="Chapters" data-testid="course-chapters">
              <h3 className={sectionHeading}>Chapters</h3>
              <ol className="border border-zinc-200 rounded-sm divide-y divide-zinc-100">
                {chapters.map((c, i) => (
                  <li key={`${c.start}-${i}`}>
                    <button
                      type="button"
                      onClick={() => seek(c.start)}
                      className="w-full flex items-baseline gap-3 px-3 py-2 text-left text-sm text-zinc-700 hover:bg-zinc-50 transition-colors"
                    >
                      <span className="font-mono text-xs tabular-nums text-zinc-500 shrink-0">
                        {formatTime(c.start)}
                      </span>
                      <span>{c.text}</span>
                    </button>
                  </li>
                ))}
              </ol>
            </section>
          )}
        </div>

        {cues.length > 0 && (
          <section aria-label="Transcript" data-testid="course-transcript">
            <h3 className={sectionHeading}>Transcript</h3>
            <div
              ref={transcriptRef}
              onWheel={noteManualScroll}
              onTouchMove={noteManualScroll}
              onKeyDown={noteManualScroll}
              className="relative max-h-96 overflow-y-auto border border-zinc-200 rounded-sm"
            >
              <ol>
                {cues.map((c, i) => {
                  const active = i === activeCue;
                  return (
                    <li key={`${c.start}-${i}`}>
                      <button
                        type="button"
                        data-cue={i}
                        aria-current={active ? 'true' : undefined}
                        onClick={() => seek(c.start)}
                        className={
                          'w-full flex items-baseline gap-3 border-l-2 px-3 py-1.5 text-left text-sm transition-colors ' +
                          (active
                            ? 'border-[#002FA7] bg-[#002FA7]/5 text-zinc-900'
                            : 'border-transparent text-zinc-600 hover:bg-zinc-50')
                        }
                      >
                        <span className="font-mono text-xs tabular-nums text-zinc-500 shrink-0">
                          {formatTime(c.start)}
                        </span>
                        <span>{c.text}</span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

export default function CoursesPage({ onBack }: Props) {
  const [catalogue, setCatalogue] = useState<CourseList | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await listCourses();
        if (cancelled) return;
        setCatalogue(data);
        setSelectedId(initialLesson(data.courses || []));
      } catch (e: any) {
        if (!cancelled) setErr(apiErrorText(e, 'Could not load the courses.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const courses = catalogue?.courses || [];
  const selected = courses.find((c) => c.id === selectedId && c.available) || null;
  const nothingToPlay = !!catalogue
    && (!catalogue.videos_dir_found || !courses.some((c) => c.available));

  const choose = (id: string) => {
    setSelectedId(id);
    rememberLesson(id);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="courses-page">
      <div className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GraduationCap size={20} className="text-[#002FA7]" />
            <h1
              className="text-lg font-semibold tracking-tight text-zinc-900"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
            >
              Courses
            </h1>
          </div>
          {onBack && (
            <button
              type="button"
              className={outlineBtn}
              onClick={onBack}
              data-testid="courses-back-button"
            >
              <ArrowLeft size={14} /> Back
            </button>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 pt-8 pb-16">
        <p className="mb-6 text-sm text-zinc-500">
          Short videos on using Draft Studio. Suggested order: D01 to D07, then D08.
        </p>

        {err && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-800 flex items-center gap-2">
            <AlertTriangle size={16} />
            {err}
          </div>
        )}

        {nothingToPlay && (
          <div
            className="mb-6 p-3 bg-zinc-50 border border-zinc-200 rounded-sm text-sm text-zinc-700 flex items-start gap-2"
            data-testid="courses-empty-note"
          >
            <Info size={16} className="mt-0.5 shrink-0 text-[#002FA7]" />
            <span>
              The videos have not been added to this server yet. An
              administrator sets <code className="font-mono text-xs">COURSE_VIDEOS_DIR</code> to
              the folder that holds them (see the README).
            </span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-zinc-500 py-10 justify-center">
            <Loader2 size={14} className="animate-spin" />
            Loading courses…
          </div>
        ) : catalogue && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[18rem_minmax(0,1fr)]">
            <ol aria-label="Lessons" className="space-y-1">
              {courses.map((c) => {
                const isSelected = selected?.id === c.id;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      data-testid={`course-item-${c.id}`}
                      disabled={!c.available}
                      aria-current={isSelected ? 'true' : undefined}
                      onClick={() => choose(c.id)}
                      className={
                        'w-full rounded-sm border px-3 py-2 text-left transition-colors ' +
                        (isSelected
                          ? 'border-[#002FA7] bg-[#002FA7]/5'
                          : c.available
                            ? 'border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50'
                            : 'border-zinc-200 bg-zinc-50 cursor-not-allowed')
                      }
                    >
                      <span className="flex items-baseline gap-2">
                        <span className="font-mono text-xs text-zinc-500 shrink-0">{c.id}</span>{' '}
                        <span
                          className={
                            'text-sm font-medium ' +
                            (c.available ? 'text-zinc-900' : 'text-zinc-500')
                          }
                        >
                          {c.title}
                        </span>
                      </span>
                      {(c.duration || !c.available) && (
                        <span className="mt-0.5 flex gap-2 text-xs text-zinc-500">
                          {c.duration && <span className="tabular-nums">{c.duration}</span>}
                          {!c.available && <span>Not added yet</span>}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ol>

            {/* Player first on narrow screens, so picking a lesson from the
                list below shows it without scrolling past eight entries. */}
            <div className="min-w-0 order-first lg:order-none">
              {selected ? (
                <LessonPlayer key={selected.id} course={selected} />
              ) : (
                <div className="flex aspect-video items-center justify-center rounded-sm border border-dashed border-zinc-300 bg-zinc-50 text-sm text-zinc-500">
                  No lesson to play yet.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
